// Companies API — typed-обёртки над 8 endpoints /api/companies/*.

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';
import { t } from '@/i18n';
import type { Page } from './tasks';

/** DTO компании. */
export interface Company {
  id: string;
  name: string;
  taxId: string;
  industry: string | null;
  website: string | null;
  /** Телефон (backend CompanyCreateRequest.phone, nullable). */
  phone?: string | null;
  /** Email (backend CompanyCreateRequest.email, nullable). */
  email?: string | null;
  address: string | null;
  /** Заметки о компании (backend VARCHAR(4000) nullable, R18.3). */
  notes: string | null;
  ownerUserId: string | null;
  departmentId: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface CompanyFilters {
  page?: number;
  size?: number;
  ownerUserId?: string;
  departmentId?: string;
  /** Живой поиск: ILIKE по выбранным полям (R33b backend, R33c frontend; legacy `search` backend игнорировал). */
  q?: string;
  /**
   * CSV-список полей поиска (U-34-D, SSOT §3.3): name|website|site_name|email|phone|tax_id|
   * license_number|ip_address. Отсутствует/пусто → все поля (обратная совместимость).
   */
  fields?: string;
  /** Сортировка «field,direction» (U-23: whitelist createdAt|updatedAt|name; направление asc|desc). */
  sort?: string;
}

export interface CompanyCreateRequest {
  name: string;
  taxId: string;
  industry?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  /** Заметки (optional, ≤4000 chars). */
  notes?: string | null;
  ownerUserId?: string | null;
  departmentId: string;
}

export interface CompanyUpdateRequest {
  name?: string;
  taxId?: string;
  industry?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  /** Заметки (optional, ≤4000 chars; null → не менять). */
  notes?: string | null;
  ownerUserId?: string | null;
  expectedUpdatedAt: string;
}

export interface CompanyAssignRequest {
  ownerUserId: string;
}

/** Companies API. */
export const companiesApi = {
  /** GET /api/companies?filters → Page<Company>. */
  async list(filters: CompanyFilters = {}): Promise<Page<Company>> {
    const { data } = await apiClient.get<Page<Company>>('/api/companies', { params: filters });
    return data;
  },

  /** GET /api/companies/{id} → Company. */
  async get(id: string): Promise<Company> {
    const { data } = await apiClient.get<Company>(`/api/companies/${id}`);
    return data;
  },

  /** POST /api/companies → Company. */
  async create(payload: CompanyCreateRequest): Promise<Company> {
    const { data } = await apiClient.post<Company>('/api/companies', payload);
    return data;
  },

  /** PUT /api/companies/{id} → Company. */
  async update(id: string, payload: CompanyUpdateRequest): Promise<Company> {
    const { data } = await apiClient.put<Company>(`/api/companies/${id}`, payload);
    return data;
  },

  /** DELETE /api/companies/{id}. */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/companies/${id}`);
  },

  /** POST /api/companies/{id}/assign → Company. */
  async assign(id: string, payload: CompanyAssignRequest): Promise<Company> {
    const { data } = await apiClient.post<Company>(`/api/companies/${id}/assign`, payload);
    return data;
  },

  /** GET /api/companies/{id}/contacts → Page<Contact>. Backend возвращает raw array (баг D25) — нормализуем. */
  async listContactsByCompany(companyId: string, page = 0, size = 50): Promise<Page<unknown>> {
    const { data } = await apiClient.get<Page<unknown> | unknown[]>(`/api/companies/${companyId}/contacts`, {
      params: { page, size },
    });
    return normalizeCrossEntityPage(data);
  },

  /** GET /api/companies/{id}/deals → Page<Deal>. Backend возвращает raw array (баг D25) — нормализуем. */
  async listDealsByCompany(companyId: string, page = 0, size = 50): Promise<Page<unknown>> {
    const { data } = await apiClient.get<Page<unknown> | unknown[]>(`/api/companies/${companyId}/deals`, {
      params: { page, size },
    });
    return normalizeCrossEntityPage(data);
  },
};

/**
 * Нормализует ответ cross-entity endpoints: backend возвращает raw array вместо Page (R25 / баг D25 — доказано curl'ом).
 * `Array.isArray(data) ? { content: data, total: data.length, page: 0, size: data.length } : data`.
 */
function normalizeCrossEntityPage<T>(data: Page<T> | T[]): Page<T> {
  if (Array.isArray(data)) {
    return { content: data, total: data.length, page: 0, size: data.length };
  }
  return data;
}

/**
 * Сколько компаний запрашиваем для построения map.
 * Backend валидирует size ≤ 100 (CompanyListQuery), 500 давал HTTP 500 (R23 / баг D21).
 */
const COMPANIES_PAGE_SIZE = 100;

/** staleTime для companies-map: справочник меняется редко. */
const COMPANIES_STALE_TIME_MS = 5 * 60 * 1000;

/** Строит Map id → name из Page<Company>. */
export function buildCompanyMap(page: Page<Company>): Map<string, string> {
  return new Map(page.content.map((c) => [c.id, c.name]));
}

/**
 * Резолвит id компании в name.
 * <p>
 * B2 (pintest-fix-B): fallback «короткий UUID» удалён — неизвестный id или
 * незагруженная map (loading/error) даёт {@code t('common.unknown')} = «Неизвестно».
 * null → «—».
 */
export function resolveCompanyName(
  companyMap: Map<string, string> | undefined,
  companyId: string | null | undefined,
): string {
  if (!companyId) return '—';
  return companyMap?.get(companyId) ?? t('common.unknown');
}

/** TanStack Query hook: Map<id, name> для резолва названий компаний. */
export function useCompanyMap(): Map<string, string> | undefined {
  const query = useQuery({
    queryKey: ['companies-map'],
    queryFn: () => companiesApi.list({ page: 0, size: COMPANIES_PAGE_SIZE }),
    staleTime: COMPANIES_STALE_TIME_MS,
    select: buildCompanyMap,
    retry: false,
  });
  return query.data;
}
